import { useState, useRef, useEffect } from "react";
import { Search, X, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useExplore } from "@/lib/ExploreContext";
import { useLang } from "@/lib/i18n";

// Smart search input. No autocomplete dropdown — submitting (Enter or button)
// runs a multi-field keyword search across all listings via ExploreContext.
export default function InlineSearch({ variant = "nav" }) {
  const ctx = useExplore() || {};
  const { setQuery, query: activeQuery } = ctx;
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { t } = useLang();
  const isHero = variant === "hero";
  const isBar = variant === "bar";

  useEffect(() => {
    setQ(activeQuery || "");
  }, [activeQuery]);

  const submit = () => {
    const term = q.trim();
    if (typeof setQuery === "function") setQuery(term);
    if (term) {
      navigate(`/?q=${encodeURIComponent(term)}`);
      setTimeout(() => {
        const target = document.querySelector("#category-results") || document.querySelector("#experiences");
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    }
    inputRef.current?.blur();
  };

  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); submit(); }
    else if (e.key === "Escape") { inputRef.current?.blur(); }
  };

  const wrapClass = isHero || isBar ? "w-full" : "flex-1 min-w-0";
  const barClass = isHero
    ? "min-h-[56px] rounded-full border-[#d7d7d7] bg-white px-3 shadow-[0_3px_14px_rgba(0,0,0,0.055)] dark:border-white/25 dark:bg-[#262321] dark:shadow-none sm:px-4"
    : isBar
    ? "min-h-[44px] px-4 rounded-full glass-btn"
    : "h-[40px] px-3 rounded-full glass-btn";
  const iconSize = isHero || isBar ? "w-5 h-5" : "w-4 h-4";
  const textSize = isHero ? "text-[16px]" : isBar ? "text-[15px]" : "text-[14px]";
  const btnSize = isHero ? "w-10 h-10" : isBar ? "w-9 h-9" : "w-9 h-9";
  const btnIcon = isHero ? "w-5 h-5" : isBar ? "w-4 h-4" : "w-4 h-4";
  const placeholder = isHero || isBar ? t("search.placeholder") : t("search.placeholderShort");

  return (
    <div className={`relative ${wrapClass}`}>
      <div className={`group flex items-center gap-2 border transition-[border-color,box-shadow,background-color] duration-200 focus-within:border-[#E30613]/70 focus-within:shadow-[0_0_0_3px_rgba(227,6,19,0.10),0_3px_14px_rgba(0,0,0,0.06)] ${barClass}`}>
        <Search className={`shrink-0 text-[#6d6d6a] dark:text-[#D4D0CA] transition-colors duration-200 group-focus-within:text-[#E30613] ${iconSize}`} strokeWidth={2} />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          placeholder={placeholder}
          aria-label={placeholder}
          autoComplete="off"
          enterKeyHint="search"
          className={`w-full min-w-0 bg-transparent outline-none text-[#171717] placeholder:text-[#858580] dark:text-[#F5F3EF] dark:placeholder:text-[#BDB8B3] selection:bg-[#E30613]/20 ${textSize}`}
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); if (typeof setQuery === "function") setQuery(""); navigate("/"); inputRef.current?.focus(); }}
            aria-label={t("search.clear")}
            className="shrink-0 inline-grid h-9 w-9 place-items-center rounded-full hover:bg-black/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b84a3a]/50"
          >
            <X className={`text-[#6d6d6a] ${iconSize}`} />
          </button>
        )}
        <button
          type="button"
          onClick={submit}
          aria-label={t("search.submit")}
          className={`shrink-0 inline-grid place-items-center rounded-full bg-[#0a0a0a] text-white transition-[background-color,transform] hover:bg-[#E30613] active:scale-95 dark:bg-[#393735] dark:text-white dark:hover:bg-[#E30613] ${btnSize}`}
        >
          <ArrowRight className={btnIcon} strokeWidth={2.5} />
        </button>
      </div>

    </div>
  );
}